import {
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import {
  certificateTypeCreateValidationType,
  certificateTypeUpdateValidationType,
  getCertifcateValidationType,
} from "@tfg/types/schemas/admin"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const useCertificateTypeQuery = ({
  fetchDataOptions,
  searchCertificateType,
  filters,
}: getCertifcateValidationType) => {
  return useQuery({
    queryKey: [
      "certificate-types",
      fetchDataOptions,
      searchCertificateType,
      filters,
    ],

    queryFn: async () => {
      const res =
        await httpClient.get<AdminResponses.CertificateTypesWithTotal>(
          "/admin/certificate-types",
          {
            params: {
              fetchDataOptions,
              searchCertificateType,
              filters: JSON.stringify(filters),
            },
          }
        )
      return res.data
    },
  })
}

export const useUpdateCertificateTypeMutation = (
  options?: UseMutationOptions<
    certificateTypeUpdateValidationType,
    unknown,
    certificateTypeUpdateValidationType
  >
) =>
  useMutation<
    certificateTypeUpdateValidationType,
    unknown,
    certificateTypeUpdateValidationType
  >({
    mutationFn: async (certificate: certificateTypeUpdateValidationType) => {
      const res = await httpClient.patch<certificateTypeUpdateValidationType>(
        `/admin/certificate-types`,
        certificate
      )
      return res.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      queryClient.invalidateQueries({ queryKey: ["certificate-types"] })
      queryClient.invalidateQueries({ queryKey: ["certificateTypesList"] })
      showSuccessNotification({
        title: "Added",
        message: "Successfully updated Certificate Type",
      })
    },
  })

export const useCreateCertificateTypeMutation = (
  options?: UseMutationOptions<
    certificateTypeCreateValidationType,
    unknown,
    certificateTypeCreateValidationType
  >
) => {
  return useMutation<
    certificateTypeCreateValidationType,
    unknown,
    certificateTypeCreateValidationType
  >({
    ...options,
    mutationFn: async (data: certificateTypeCreateValidationType) => {
      const res = await httpClient.post<certificateTypeCreateValidationType>(
        "/admin/certificate-types",
        { ...data }
      )
      return res.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      queryClient.invalidateQueries({ queryKey: ["certificate-types"] })
      queryClient.invalidateQueries({ queryKey: ["certificateTypesList"] })
      showSuccessNotification({
        title: "Added",
        message: "Successfully created certificate type",
      })
    },
  })
}

export const useDeleteCertificateTypeMutation = (
  options?: UseMutationOptions<void, unknown, string>
) => {
  return useMutation({
    ...options,
    mutationFn: async (id: string) => {
      const res = await httpClient.delete(`/admin/certificate-types/${id}`)
      return res.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      queryClient.invalidateQueries({ queryKey: ["certificate-types"] })
      queryClient.invalidateQueries({ queryKey: ["certificateTypesList"] })
      showSuccessNotification({
        title: "Deleted",
        message: "Successfully deleted certificate type",
      })
    },
  })
}
